<template>
  <div>
    <!--Loading-->
    <b-loading :is-full-page="true" :active.sync="isPendingExplanationsLoading"></b-loading>

    <PageTitle title="Pending Explanations" />
    <div class="columns">
      <div class="column is-12">
        <div class="card">
          <div class="card-content">
            <!-- Filters Section -->
            <div class="columns is-multiline mb-4">
              <div class="column is-12">
                <h4 class="title is-5">Filters</h4>
              </div>
              
              <!-- Grade Subject Filter -->
              <div class="column is-4">
                <b-field label="Grade & Subject">
                  <b-select 
                    v-model="filterData.grade_subject" 
                    placeholder="Select Grade & Subject"
                    expanded
                    @input="onGradeSubjectChange"
                  >
                    <option value="">All Grade Subjects</option>
                    <option 
                      v-for="gradeSubject in fetchGradeSubjects" 
                      :key="gradeSubject._id" 
                      :value="gradeSubject._id"
                    >
                      {{ gradeSubject.grade.name }} - {{ gradeSubject.subject.name }}
                    </option>
                  </b-select>
                </b-field>
              </div>

              <!-- Category Filter -->
              <div class="column is-4">
                <b-field label="Category">
                  <b-select 
                    v-model="filterData.category" 
                    placeholder="Select Category"
                    expanded
                    @input="onCategoryChange"
                    :disabled="!filterData.grade_subject"
                  >
                    <option value="">All Categories</option>
                    <option 
                      v-for="category in availableCategories" 
                      :key="category._id" 
                      :value="category._id"
                    >
                      {{ category.name }}
                    </option>
                  </b-select>
                </b-field>
              </div>

              <!-- Skill Filter -->
              <div class="column is-4">
                <b-field label="Skill">
                  <b-select 
                    v-model="filterData.skill_id" 
                    placeholder="Select Skill"
                    expanded
                    @input="onSkillChange"
                    :disabled="!filterData.category"
                  >
                    <option value="">All Skills</option>
                    <option 
                      v-for="skill in availableSkills" 
                      :key="skill._id" 
                      :value="skill._id"
                    >
                      {{ skill.name }}
                    </option>
                  </b-select>
                </b-field>
              </div>

              <!-- Filter Actions -->
              <div class="column is-12">
                <div class="buttons">
                  <b-button 
                    @click="applyFilters" 
                    type="is-primary" 
                    icon-left="filter"
                    :loading="isFiltersLoading"
                  >
                    Apply Filters
                  </b-button>
                  <b-button 
                    @click="clearFilters" 
                    type="is-light" 
                    icon-left="refresh"
                    :loading="isFiltersLoading"
                  >
                    Clear Filters
                  </b-button>
                </div>
              </div>
            </div>

            <hr />

            <!-- Pagination Controls -->
            <div class="columns" v-if="pagination.total > 0">
              <div class="column is-9">
                <b-pagination
                  :total="pagination.total"
                  v-model="currentPage"
                  :per-page="pagination.per_page"
                  aria-next-label="Next page"
                  aria-previous-label="Previous page"
                  aria-page-label="Page"
                  aria-current-label="Current page"
                  @change="onPageChange"
                >
                </b-pagination>
              </div>
              <div class="column is-3">
                <b-numberinput
                  v-model="pageInputValue"
                  :controls="false"
                  placeholder="Page number"
                  @input="onPageInputChange"
                  :max="pagination.total_pages"
                  :min="1"
                ></b-numberinput>
              </div>
            </div>

            <!-- Pagination Info -->
            <div class="columns" v-if="pagination.total > 0">
              <div class="column is-12">
                <p class="has-text-grey">
                  Showing {{ pagination.from }} to {{ pagination.to }} of {{ pagination.total }} results
                  <span v-if="hasActiveFilters" class="has-text-weight-bold">(filtered)</span>
                </p>
              </div>
            </div>

            <!--Add/Edit Question Modal-->
            <b-modal :active.sync="modalSetQuestionActive" has-modal-card trap-focus :full-screen="true"
              :destroy-on-hide="true" aria-role="dialog" aria-modal>
              <set-question :skill_id="current_skill_id" :question_id="edit_question_id"
                @over="onQuestionModalClose"></set-question>
            </b-modal>

            <!-- Pending Explanations Table -->
            <UpTable 
              :data="fetchPendingExplanations" 
              :loading="isPendingExplanationsLoading"
            >
              <UpTableColumn v-slot="props" label="Question">
                <div v-html="props.row.question" class="question-content"></div>
              </UpTableColumn>
              
              <UpTableColumn v-slot="props" label="Skill">
                {{ props.row.skill ? props.row.skill.name : 'N/A' }}
              </UpTableColumn>
              
              <UpTableColumn v-slot="props" label="Category">
                {{ props.row.skill && props.row.skill.category ? props.row.skill.category.name : 'N/A' }}
              </UpTableColumn>
              
              <UpTableColumn v-slot="props" label="Grade & Subject">
                <span v-if="props.row.skill && props.row.skill.category && props.row.skill.category.grade_subject">
                  {{ props.row.skill.category.grade_subject.grade.name }} - 
                  {{ props.row.skill.category.grade_subject.subject.name }}
                </span>
                <span v-else>N/A</span>
              </UpTableColumn>

              <UpTableColumn v-slot="props" field="type" label="Type" sortable>
                <b-tag :type="getQuestionTypeColor(props.row.type)">
                  {{ getQuestionTypeLabel(props.row.type) }}
                </b-tag>
              </UpTableColumn>

              <UpTableColumn v-slot="props" field="difficulty" label="Difficulty" sortable>
                <b-tag :type="getDifficultyColor(props.row.difficulty)">
                  {{ getDifficultyLabel(props.row.difficulty) }}
                </b-tag>
              </UpTableColumn>

              <UpTableColumn v-slot="props" field="created_at" label="Created At" sortable>
                {{ $common.formatDate(props.row.created_at) }}
              </UpTableColumn>

              <UpTableColumn v-slot="props" label="Actions">
                <div class="buttons">
                  <router-link
                    v-if="getQuestionRouteParams(props.row)"
                    target="_blank"
                    :to="{ 
                      name: 'quiz', 
                      query: { first_question: props.row._id }, 
                      params: getQuestionRouteParams(props.row)
                    }"
                  >
                    <b-button 
                      type="is-dark" 
                      size="is-small" 
                      icon-left="eye"
                      title="View Question"
                    >
                    </b-button>
                  </router-link>
                  
                  <b-button 
                    @click="editQuestion(props.row._id, props.row.skill ? props.row.skill._id : null)" 
                    type="is-primary" 
                    size="is-small" 
                    icon-left="pencil"
                    title="Edit Question"
                  >
                  </b-button>
                </div>
              </UpTableColumn>

              <template #empty>
                <empty-table message="No pending explanations found."></empty-table>
              </template>
            </UpTable>

            <!-- Show message when no explanations exist -->
            <div v-if="pagination.total === 0 && !isPendingExplanationsLoading" class="has-text-centered">
              <p class="title is-5">No pending explanations</p>
              <p class="subtitle is-6" v-if="hasActiveFilters">
                No questions match the current filters. Try adjusting your filters or 
                <a @click="clearFilters" class="has-text-primary">clear all filters</a>.
              </p>
              <p class="subtitle is-6" v-else>All questions have explanations or no questions exist</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
//Vuex
import { mapGetters, mapActions } from 'vuex'
import PageTitle from '@/components/PageTitle'
import EmptyTable from '@/components/EmptyTable'
import SetQuestion from '@/components/Question/SetQuestion'

export default {
  name: 'PendingExplanations',
  components: {
    PageTitle,
    EmptyTable,
    SetQuestion
  },
  data() {
    return {
      modalSetQuestionActive: false,
      edit_question_id: null,
      current_skill_id: null,
      pageInputValue: 1,
      isFiltersLoading: false,
      filterData: {
        grade_subject: '',
        category: '',
        skill_id: ''
      },
      availableCategories: [],
      availableSkills: []
    }
  },
  computed: {
    ...mapGetters([
      'fetchPendingExplanations', 
      'fetchPendingExplanationsPagination', 
      'isPendingExplanationsLoading',
      'fetchPendingExplanationsFilters',
      'getTeacherToken',
      'fetchGradeSubjects',
      'fetchCategories',
      'fetchSkills'
    ]),
    
    pagination() {
      return this.fetchPendingExplanationsPagination
    },
    
    currentPage: {
      get() {
        return this.pagination.current_page
      },
      set(value) {
        // This will be handled by onPageChange method
        this.pageInputValue = value
      }
    },

    hasActiveFilters() {
      const filters = this.fetchPendingExplanationsFilters
      return !!(filters.grade_subject || filters.category || filters.skill_id)
    }
  },
  methods: {
    ...mapActions([
      'loadPendingExplanations',
      'changePendingExplanationsPage',
      'refreshPendingExplanations',
      'applyPendingExplanationsFilters',
      'clearPendingExplanationsFilters',
      'loadGradeSubjects',
      'loadCategories',
      'loadSkills'
    ]),
    
    async onPageChange(page) {
      try {
        await this.changePendingExplanationsPage({
          token: this.getTeacherToken,
          page: page
        })
        this.pageInputValue = page
      } catch (err) {
        this.$buefy.toast.open({
          message: err.response?.data || 'Failed to load page',
          type: 'is-danger'
        })
      }
    },

    async onPageInputChange(value) {
      if (value && value >= 1 && value <= this.pagination.total_pages && value !== this.currentPage) {
        await this.onPageChange(value)
      }
    },

    async onGradeSubjectChange() {
      this.filterData.category = ''
      this.filterData.skill_id = ''
      this.availableCategories = []
      this.availableSkills = []

      if (this.filterData.grade_subject) {
        try {
          this.isFiltersLoading = true
          await this.loadCategories({
            token: this.getTeacherToken,
            filter: {
              grade_subject: this.filterData.grade_subject
            }
          })
          this.availableCategories = this.fetchCategories
        } catch (err) {
          this.$buefy.toast.open({
            message: 'Failed to load categories',
            type: 'is-danger'
          })
        } finally {
          this.isFiltersLoading = false
        }
      }
    },

    async onCategoryChange() {
      this.filterData.skill_id = ''
      this.availableSkills = []

      if (this.filterData.category) {
        try {
          this.isFiltersLoading = true
          await this.loadSkills({
            token: this.getTeacherToken,
            filter: {
              category: this.filterData.category
            }
          })
          this.availableSkills = this.fetchSkills
        } catch (err) {
          this.$buefy.toast.open({
            message: 'Failed to load skills',
            type: 'is-danger'
          })
        } finally {
          this.isFiltersLoading = false
        }
      }
    },

    onSkillChange() {
      // Skill change doesn't require additional loading
    },

    async applyFilters() {
      try {
        this.isFiltersLoading = true
        const filters = {
          grade_subject: this.filterData.grade_subject || null,
          category: this.filterData.category || null,
          skill_id: this.filterData.skill_id || null
        }
        
        await this.applyPendingExplanationsFilters({
          token: this.getTeacherToken,
          filters
        })
        this.pageInputValue = 1
      } catch (err) {
        this.$buefy.toast.open({
          message: err.response?.data || 'Failed to apply filters',
          type: 'is-danger'
        })
      } finally {
        this.isFiltersLoading = false
      }
    },

    async clearFilters() {
      try {
        this.isFiltersLoading = true
        this.filterData = {
          grade_subject: '',
          category: '',
          skill_id: ''
        }
        this.availableCategories = []
        this.availableSkills = []
        
        await this.clearPendingExplanationsFilters({
          token: this.getTeacherToken
        })
        this.pageInputValue = 1
      } catch (err) {
        this.$buefy.toast.open({
          message: err.response?.data || 'Failed to clear filters',
          type: 'is-danger'
        })
      } finally {
        this.isFiltersLoading = false
      }
    },

    async onQuestionModalClose() {
      this.modalSetQuestionActive = false
      this.edit_question_id = null
      this.current_skill_id = null
      
      // Refresh current page to reflect any changes
      try {
        await this.refreshPendingExplanations({
          token: this.getTeacherToken
        })
      } catch (err) {
        // Silent refresh failure
        console.error('Failed to refresh after question edit:', err)
      }
    },
    
    editQuestion(questionId, skillId) {
      this.edit_question_id = questionId
      this.current_skill_id = skillId
      this.modalSetQuestionActive = true
    },

    getQuestionRouteParams(question) {
      // Extract route parameters from question data
      if (question.skill && 
          question.skill.category && 
          question.skill.category.grade_subject) {
        return {
          kid_id: '6049c7ebe2f97e0013da87ff', // Default kid ID like in QuestionTable
          grade_subject_id: question.skill.category.grade_subject._id,
          category_id: question.skill.category._id,
          skill_id: question.skill._id
        }
      }
      return null
    },

    getQuestionTypeLabel(type) {
      const types = {
        'MCQ': 'Multiple Choice',
        'DQ': 'Descriptive',
        'TF': 'True/False',
        'CL': 'Custom Layout',
        'DRAGDROP': 'Drag & Drop',
        'PATTERN': 'Pattern',
        'ORDERING': 'Ordering',
        'DIVIDER': 'Divider',
        'MATCHING': 'Matching'
      }
      return types[type] || type
    },

    getQuestionTypeColor(type) {
      const colors = {
        'MCQ': 'is-primary',
        'DQ': 'is-info',
        'TF': 'is-success',
        'CL': 'is-warning',
        'DRAGDROP': 'is-danger',
        'PATTERN': 'is-light',
        'ORDERING': 'is-dark',
        'DIVIDER': 'is-link',
        'MATCHING': 'is-primary'
      }
      return colors[type] || 'is-light'
    },

    getDifficultyLabel(difficulty) {
      const labels = {
        1: 'Easy',
        2: 'Medium',
        3: 'Hard'
      }
      return labels[difficulty] || 'Unknown'
    },

    getDifficultyColor(difficulty) {
      const colors = {
        1: 'is-success',
        2: 'is-warning',
        3: 'is-danger'
      }
      return colors[difficulty] || 'is-light'
    }
  },

  async created() {
    try {
      // Load grade subjects for filter dropdown
      await this.loadGradeSubjects({ 
        token: this.getTeacherToken,
        filter: {}
      })

      // Load initial pending explanations data
      await this.loadPendingExplanations({ 
        token: this.getTeacherToken,
        page: 1,
        per_page: 20
      })
      this.pageInputValue = this.currentPage
    } catch (err) {
      this.$buefy.toast.open({
        message: err.response?.data || 'Failed to load data',
        type: 'is-danger'
      })
    }
  },

  watch: {
    currentPage(newPage) {
      this.pageInputValue = newPage
    },

    // Sync filter data with store filters
    fetchPendingExplanationsFilters: {
      handler(newFilters) {
        if (newFilters) {
          this.filterData = {
            grade_subject: newFilters.grade_subject || '',
            category: newFilters.category || '',
            skill_id: newFilters.skill_id || ''
          }
        }
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
.question-content {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.buttons {
  display: flex;
  gap: 0.5rem;
}

@media screen and (max-width: 768px) {
  .question-content {
    max-width: 200px;
  }
  
  .buttons {
    flex-direction: column;
  }
}
</style> 